// Sidebar store — preloads sidebar data eagerly so it's instant when opened.
// Data is loaded once auth is ready, and can be refreshed on demand.

import { isUserAnonymous } from './auth.svelte.js';
import {
  getRecentBinders,
  normalizeToShortUrl,
  getPinnedBinders,
  getMyBinders,
} from '../services/recent-binders.js';
import { getActiveTrades } from '../services/user-data.js';

export const sidebarState = $state({
  recentBinders: [],
  myBinders: [],
  openTrades: [],
  awaitingTrades: [],
  pinnedUrls: [],
  loaded: false,
});

let loading = false;

export async function loadSidebarData() {
  if (loading) return;
  loading = true;

  try {
    const signedIn = !isUserAnonymous();
    const [binders, { openTrades, awaitingTrades }, pinned, claimed] = await Promise.all([
      getRecentBinders(),
      signedIn ? getActiveTrades() : { openTrades: [], awaitingTrades: [] },
      getPinnedBinders(),
      getMyBinders(),
    ]);

    sidebarState.openTrades = openTrades;
    sidebarState.awaitingTrades = awaitingTrades;
    sidebarState.pinnedUrls = pinned.map((u) => normalizeToShortUrl(u).toLowerCase());
    sidebarState.myBinders = claimed;

    // Build set of my binder URLs for filtering from recents
    const myBinderUrls = new Set(claimed.map((b) => normalizeToShortUrl(b.url).toLowerCase()));

    // Merge trade binders into recent binders
    const allTrades = [...openTrades, ...awaitingTrades];
    const binderMap = new Map();

    for (const trade of allTrades) {
      if (trade.binderUrl) {
        const key = normalizeToShortUrl(trade.binderUrl)?.toLowerCase() || '';
        if (key && !binderMap.has(key)) {
          binderMap.set(key, {
            url: trade.binderUrl,
            name: trade.binderName || 'Unknown',
            owner: trade.ownerName || null,
            lastAccessed: trade.updatedAt?.getTime?.() || Date.now(),
          });
        }
      }
    }

    for (const binder of binders) {
      const key = normalizeToShortUrl(binder.url)?.toLowerCase() || '';
      if (!key) continue;
      const existing = binderMap.get(key);
      if (!existing) {
        binderMap.set(key, binder);
      } else {
        binderMap.set(key, {
          url: binder.url,
          name: binder.name || existing.name,
          owner: binder.owner || existing.owner,
          lastAccessed: Math.max(binder.lastAccessed || 0, existing.lastAccessed || 0),
        });
      }
    }

    const pinnedSet = new Set(sidebarState.pinnedUrls);
    const merged = Array.from(binderMap.values()).filter(
      (b) => !myBinderUrls.has(normalizeToShortUrl(b.url).toLowerCase())
    );
    merged.sort((a, b) => {
      const aPinned = pinnedSet.has(normalizeToShortUrl(a.url).toLowerCase());
      const bPinned = pinnedSet.has(normalizeToShortUrl(b.url).toLowerCase());
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return (b.lastAccessed || 0) - (a.lastAccessed || 0);
    });

    sidebarState.recentBinders = merged;
    sidebarState.loaded = true;
  } catch (e) {
    console.warn('[Sidebar] Failed to load data:', e);
  } finally {
    loading = false;
  }
}

export function isPinned(url) {
  return sidebarState.pinnedUrls.includes(normalizeToShortUrl(url).toLowerCase());
}
